import React, { useEffect, useState } from "react";
import Header from "./Header";
import FloatingLabelInput from "./FloatingLabelInput";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { auth } from "../utils/firebase";
import { useDispatch, useSelector } from "react-redux";
import { addUser } from "../store/userSlice";
import { useNavigate } from "react-router-dom";

const AuthForm = () => {
  const [isSignup, setIsSignUp] = useState(false);
  const { data: user, isAuthChecked } = useSelector((store) => store.user);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      fullName: "",
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      fullName: isSignup
        ? Yup.string()
            .min(3, "Name must have at least 3 characters")
            .required("Full Name is required")
        : null,
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    }),
    onSubmit: (values) => {
      const { fullName, email, password } = values;
      setIsLoading(true);

      if (isSignup) {
        createUserWithEmailAndPassword(auth, email, password)
          .then((userCredential) => {
            const user = userCredential.user;
            updateProfile(user, {
              displayName: fullName,
            })
              .then(() => {
                const { displayName, email, uid } = auth.currentUser;
                dispatch(addUser({ uid, email, displayName }));
              })
              .catch((error) => {
                console.log(error);
              })
              .finally(() => setIsLoading(false));
          })
          .catch((error) => {
            const errorCode = error.code;
            switch (errorCode) {
              case "auth/email-already-in-use":
                setErrorMessage(
                  "This email is already associated with an account.",
                );
                break;
              case "auth/wrong-password":
                setErrorMessage("Incorrect password. Please try again.");
                break;
              case "auth/invalid-email":
                setErrorMessage("The email address is invalid.");
                break;
              default:
                setErrorMessage("An error occurred. Please try again.");
            }
            setIsLoading(false);
            setTimeout(() => {
              setErrorMessage("");
            }, 2000);
          });
      } else {
        signInWithEmailAndPassword(auth, email, password)
          .then(() => {
            const { displayName, email, uid } = auth.currentUser;
            dispatch(addUser({ uid, email, displayName }));
          })
          .catch((error) => {
            const errorCode = error.code;

            switch (errorCode) {
              case "auth/invalid-credential":
                setErrorMessage("Wrong email or password");
                break;
              case "auth/user-not-found":
                setErrorMessage("No user found with this email.");
                break;
              case "auth/too-many-requests":
                setErrorMessage(
                  "Too many login attempts. Please try again later.",
                );
                break;
              default:
                setErrorMessage("An error occurred. Please try again.");
            }
            setIsLoading(false);
            setTimeout(() => {
              setErrorMessage("");
            }, 2000);
          })
          .finally(() => setIsLoading(false));
      }
    },
  });

  useEffect(() => {
    if (isAuthChecked && user) {
      navigate("/browse");
    }
  }, [isAuthChecked, navigate, user]);

  if (!isAuthChecked) {
    return (
      <>
        <Header />
        <main className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
          <div className="h-14 w-14 border-4 border-neutral-700 border-t-red-600 rounded-full animate-spin"></div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="relative min-h-svh sm:min-h-screen overflow-hidden bg-gradient-to-b from-black via-transparent to-transparent">
        <div>
          <img
            className="absolute object-cover h-screen w-full"
            alt={`Background for Netflix ${isSignup ? "signup" : "login"}`}
            role="presentation"
            src="/img/loginBg.jpg"
          />
        </div>

        <div className="absolute inset-0 bg-black bg-opacity-75 lg:opacity-60"></div>

        <form
          onSubmit={formik.handleSubmit}
          className="absolute w-[90%] sm:w-[80%] md:w-[70%] lg:w-[50%] xl:w-[40%] 2xl:w-[30%] flex flex-col gap-y-4 rounded bg-black bg-opacity-90 lg:bg-opacity-80 text-white py-4 lg:py-10 px-8 lg:px-16 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          {/* Title */}
          <h1 className="text-3xl font-semibold mb-4">
            {isSignup ? "Create Account" : "Sign In"}
          </h1>

          {/* Full Name */}
          {isSignup && (
            <FloatingLabelInput
              label="Full Name"
              type="text"
              name="fullName"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.fullName && formik.errors.fullName}
            />
          )}

          {/* Email */}
          <FloatingLabelInput
            label="Email"
            type="text"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && formik.errors.email}
          />

          {/* Password */}
          <FloatingLabelInput
            label="Password"
            type="password"
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && formik.errors.password}
          />

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-600/10 border border-red-600 text-red-400 text-sm px-4 py-2 rounded-md">
              {errorMessage}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700 transition-colors p-3 rounded-md font-semibold disabled:opacity-70"
          >
            {isLoading
              ? isSignup
                ? "Creating Account..."
                : "Signing In..."
              : isSignup
                ? "Sign Up"
                : "Sign In"}
          </button>

          {/* Toggle Links */}
          {isSignup ? (
            <p className="text-gray-400 text-sm text-center mt-2">
              Already have an account?{" "}
              <span
                onClick={() => {
                  setIsSignUp(false);
                  formik.resetForm();
                }}
                className="font-semibold text-white hover:underline cursor-pointer"
              >
                Sign in
              </span>
            </p>
          ) : (
            <>
              <p
                onClick={() => navigate("/forgot-password")}
                className="text-center cursor-pointer text-gray-400 hover:underline text-sm"
              >
                Forgot Password?
              </p>

              <p className="text-gray-400 text-sm text-center">
                New here?{" "}
                <span
                  onClick={() => {
                    setIsSignUp(true);
                    formik.resetForm();
                  }}
                  className="font-semibold text-white hover:underline cursor-pointer"
                >
                  Create account
                </span>
              </p>
            </>
          )}

          {/* Divider */}
          <div className="flex items-center gap-4 my-2">
            <div className="flex-1 h-px bg-neutral-700"></div>
            <span className="text-neutral-500 text-xs tracking-wide">OR</span>
            <div className="flex-1 h-px bg-neutral-700"></div>
          </div>

          {/* Demo Account Card */}
          <div className="bg-neutral-800/70 border border-neutral-700 rounded-xl p-4 flex flex-col gap-3">
            <div>
              <h4 className="text-sm font-semibold text-neutral-200">
                Try the demo account
              </h4>
              <p className="text-xs text-neutral-400">
                Instantly explore the app without creating an account.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setIsSignUp(false);
                setTimeout(() => {
                  formik.setValues({
                    fullName: "",
                    email: "demo@example.com",
                    password: "Pass1234",
                  });
                }, 0);
              }}
              className="bg-neutral-700 hover:bg-neutral-600 transition-colors text-sm font-medium py-2 rounded-md"
            >
              Use Demo Credentials
            </button>
          </div>
        </form>
      </main>
    </>
  );
};

export default AuthForm;
